import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider, MenuItem, Select } from '@mui/material';
import { control, menu } from './CaseWorkflow/filterStyles';
import { Project, listProjects, projectLabel, setStoredProjectId } from '../services/projectService';
import { dsVar } from '../theme/tokens';

interface WorkspaceSelectorProps {
    /** The project currently open, from the route. */
    currentProjectId?: string;
}

/** The sentinel behind "View all projects" — not a project id. */
const ALL_PROJECTS = '__all__';

/**
 * The workspace switcher in the top bar.
 *
 * Since login now lands straight in a project rather than on the projects grid,
 * this is the primary way to reach another one; the grid itself stays one click
 * away at the bottom of the list.
 */
const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({ currentProjectId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [projects, setProjects] = useState<Project[]>([]);

    useEffect(() => {
        let active = true;
        listProjects()
            .then((list) => { if (active) setProjects(list); })
            // The page is still usable without the switcher, so a failure here
            // leaves it showing the current project alone rather than an error.
            .catch((error) => console.error('Failed to load projects:', error));
        return () => { active = false; };
    }, []);

    const handleChange = (value: string) => {
        if (value === ALL_PROJECTS) {
            navigate('/intelligence-hub');
            return;
        }
        if (!value || value === currentProjectId) return;

        setStoredProjectId(value);
        // Switching workspace keeps the screen the user is on. The query string
        // is dropped: filter values name runs and flows of the old project.
        const suffix = location.pathname.endsWith('/case-workflow') ? '/case-workflow' : '';
        navigate(`/analytics/projects/${value}${suffix}`);
    };

    const known = projects.some((project) => project._id === currentProjectId);

    return (
        <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-fg-2 uppercase tracking-widest whitespace-nowrap">
                Change workspace
            </span>
            <Select
                value={currentProjectId ?? ''}
                onChange={(e) => handleChange(String(e.target.value))}
                size="small"
                displayEmpty
                sx={{ ...control, minWidth: 220, maxWidth: 340 }}
                MenuProps={menu}
                // A deep link, or a project past the first page, is not in the
                // list yet — say so rather than rendering an empty control.
                renderValue={() => {
                    const current = projects.find((project) => project._id === currentProjectId);
                    if (current) return projectLabel(current);
                    return currentProjectId ? 'Current project' : 'Choose a project';
                }}
            >
                {!known && currentProjectId && (
                    <MenuItem value={currentProjectId} sx={{ fontSize: '0.8125rem' }}>
                        Current project
                    </MenuItem>
                )}
                {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id} sx={{ fontSize: '0.8125rem' }}>
                        {projectLabel(project)}
                    </MenuItem>
                ))}
                <Divider sx={{ borderColor: dsVar('line') }} />
                <MenuItem value={ALL_PROJECTS} sx={{ fontSize: '0.8125rem', color: dsVar('accent-fg') }}>
                    View all projects
                </MenuItem>
            </Select>
        </div>
    );
};

export default WorkspaceSelector;
